import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { InvestorsGroupAutocomplete } from "components/Measure-data/Groups-form/InvestorsGroupAutocomplete";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import useCurrency from "hooks/tagger-hooks/useCurrency";
import useDate from "hooks/tagger-hooks/useDate";
import useInvestors from "hooks/tagger-hooks/useInvestors";
import useMeasure from "hooks/tagger-hooks/useMeasure";
import useRounds from "hooks/tagger-hooks/useRounds";
import { IApiServiceError } from "store/services";
import {
  ApiValidationErrors,
  IProcessedTag,
  MeasureParameter,
  TaggerSplitBy,
  TaggerSplitByGroup,
  TagPhrasePayload
} from "store/types";
import { IFileData } from "store/types/tagger/api";
import toIsoMidday from "utils/to-iso-midday";

import { ALL_INVESTORS_VALUE } from "../../../../constants/tagger";
import {
  useAccountSelector,
  useDateFormatSelect,
  useInvestorsSelector,
  useTaggerDocumentSelector
} from "../../../../store";
import { TaggerDocument } from "../../../../store/types";
import { Dropdown } from "../../General/Dropdown";

type InvestorOption = { value: number; label: string };
type SeparatorOption = { value: "__separator__"; label: string; isSeparator: true };
type InvestorOptionType = InvestorOption | SeparatorOption;

export interface TagMenuProps extends PropsWithChildren {
  docId: TaggerDocument["id"];
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  onSelect: (
    data: Omit<
      TagPhrasePayload,
      "deal_id" | "document_id" | "is_measure_global_value" | "file_polygon_id" | "value"
    > & { investor_ids?: number[] }
  ) => Promise<void>;
  selectedPolygonsTag?: IProcessedTag;
  selectedValue: number;
  onValueChange?: (value: number) => void;
  numberFormat?: "eu" | "us";
  documentFileData: IFileData;
}

const TagMenuContent = ({
  docId,
  open,
  onClose,
  onSelect,
  selectedPolygonsTag,
  selectedValue,
  onValueChange,
  numberFormat = "us",
  documentFileData
}: TagMenuProps) => {
  const { allow_split_by_group } = useAccountSelector() || {};
  const dateFormat = useDateFormatSelect();
  const investors = useInvestorsSelector();
  const isSplittable = useTaggerDocumentSelector(docId).isSplittable;

  const {
    options: roundOpts,
    loading: roundLoading,
    value: roundVal,
    onChange: roundOnChange
  } = useRounds(docId, selectedPolygonsTag);
  const {
    options: invOptions,
    loading: invLoading,
    value: invValue,
    onChange: invOnChange
  } = useInvestors(docId, selectedPolygonsTag);
  const {
    options: currOpts,
    loading: currLoading,
    value: currValue,
    onChange: currOnChange
  } = useCurrency(docId, selectedPolygonsTag);

  const { options: measureOptions, value: measureValue, onChange: measureOnChange } = useMeasure(selectedPolygonsTag);
  const { loading: dateLoading, value: dateValue, onChange: dateOnChange } = useDate(docId, selectedPolygonsTag);
  const [createdInvestors, setCreatedInvestors] = useState<{ value: number; label: string }[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [splitType, setSplitType] = useState<TaggerSplitBy | null>(null);
  const [splitTypeGroup, setSplitTypeGroup] = useState<TaggerSplitByGroup>("entire_commitment" as TaggerSplitByGroup);
  const [isDraft, setIsDraft] = useState(selectedPolygonsTag?.is_draft || false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const investorsFromStore = useMemo(
    () => investors.map((investor) => ({ value: investor.id, label: investor.name })),
    [investors]
  );

  const currentMeasure = useMemo(
    () => measureOptions.find((m) => m.id === measureValue) || null,
    [measureValue, measureOptions]
  );
  const isMeasureGlobalValue = currentMeasure?.is_global_value || false;

  const isChild = selectedPolygonsTag?.is_child || false;
  const isGroupParent = !isChild && !!selectedPolygonsTag?.children;

  // Clear validation errors when menu closes
  useEffect(() => {
    if (!open) {
      setValidationErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (validationErrors.split_by) {
      setValidationErrors({});
    }
  }, [selectedGroups]);

  // Map API error keys to form field names
  const mapApiErrorsToFields = (apiErrors: ApiValidationErrors): Record<string, string> => {
    const mappedErrors: Record<string, string> = {};

    // Map API snake_case keys to form field names
    const errorMapping: Record<string, string> = {
      measure_id: "measure",
      investor_id: "investor",
      round_id: "round",
      value: "value",
      date: "date",
      accounting_date: "accountingDate",
      currency: "currency"
    };

    Object.keys(apiErrors).forEach((apiKey) => {
      const fieldKey = errorMapping[apiKey] || apiKey;
      if (apiErrors[apiKey] && apiErrors[apiKey].length > 0) {
        mappedErrors[fieldKey] = apiErrors[apiKey][0];
      }
    });

    return mappedErrors;
  };

  const investorsOptions = useMemo<InvestorOptionType[]>(() => {
    // Filter out ALL_INVESTORS_VALUE if:
    // 1. measure is global value AND document is not splittable, OR
    // 2. selectedPolygonsTag is a child tag
    // const shouldFilterAllInvestors = (isMeasureGlobalValue && !isSplittable) || isChild;
    const shouldFilterAllInvestors = isMeasureGlobalValue || !isSplittable || isChild;
    const dealInvestors = shouldFilterAllInvestors
      ? invOptions.filter((opt) => opt.value != ALL_INVESTORS_VALUE)
      : invOptions;
    const accountInvestors = [...investorsFromStore, ...createdInvestors];

    const options: InvestorOptionType[] = [];

    if (dealInvestors.length > 0) {
      options.push({ value: "__separator__", label: "DEAL INVESTORS", isSeparator: true });
      options.push(...dealInvestors);
    }
    if (accountInvestors.length > 0) {
      options.push({ value: "__separator__", label: "ACCOUNT INVESTORS", isSeparator: true });
      options.push(...accountInvestors);
    }

    return options;
  }, [invOptions, createdInvestors, isMeasureGlobalValue, investorsFromStore, isSplittable, isChild]);

  const getFieldError = (fieldName: string) => !!validationErrors[fieldName];
  const getFieldHelperText = (fieldName: string) => validationErrors[fieldName] || "";

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!currentMeasure) {
      errors.measure = "Parameter is required";
    }
    if (selectedValue === 0 || selectedValue === undefined || selectedValue === null) {
      errors.value = "Value is required";
    }
    if (!dateValue) {
      errors.date = "Date is required";
    }
    if (!currValue) {
      errors.currency = "Currency is required";
    }
    if (!isGroupParent && !currentMeasure?.is_global_value && !invValue && !allow_split_by_group) {
      errors.investor = "Please select an investor";
    }
    if (
      !isGroupParent &&
      !currentMeasure?.is_global_value &&
      allow_split_by_group &&
      !selectedGroups.length &&
      !invValue
    ) {
      errors.investorsGroup = "Please select at least one investor group";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }
    return true;
  };

  const handleApply = async () => {
    if (!validateForm()) {
      return;
    }
    if (!currentMeasure || !dateValue || !currValue) {
      return;
    }

    // Clear previous errors
    setValidationErrors({});

    try {
      if (allow_split_by_group && !currentMeasure?.is_global_value && Boolean(selectedGroups.length)) {
        await onSelect({
          measure_id: currentMeasure.id,
          is_draft: isDraft,
          split_by: splitTypeGroup || undefined,
          date: dateValue || "",
          investor_id: null,
          investor_ids: investors
            .filter((investor) => selectedGroups.includes(investor.group!))
            .map((investor) => investor.id),
          round_id: roundVal,
          currency: currValue
        });
      } else {
        await onSelect({
          measure_id: currentMeasure.id,
          is_draft: isDraft,
          split_by: invValue === ALL_INVESTORS_VALUE ? "all_investors" : splitType || undefined,
          date: dateValue || "",
          investor_id: invValue,
          round_id: roundVal,
          currency: currValue
        });
      }
      setSplitType(null);
    } catch (err) {
      // Extract errors from different possible error structures
      let apiErrors: ApiValidationErrors = {};

      const error = err as
        | IApiServiceError
        | Error
        | { errors?: ApiValidationErrors; message?: string; response?: { data?: { errors?: ApiValidationErrors } } };

      // Check for errors property directly (ApiServiceError instance or AltiusErrors)
      if (error && typeof error === "object" && "errors" in error) {
        const errors = error.errors;
        if (errors && typeof errors === "object" && !Array.isArray(errors)) {
          apiErrors = errors as ApiValidationErrors;
        }
      }
      // Check for nested response structure (AxiosError)
      else if (error && typeof error === "object" && "response" in error && error.response?.data?.errors) {
        apiErrors = error.response.data.errors as ApiValidationErrors;
      }

      // Only show validation errors if we have them, otherwise let the error propagate
      if (Object.keys(apiErrors).length > 0) {
        const mappedErrors = mapApiErrorsToFields(apiErrors);
        setValidationErrors(mappedErrors);
      } else {
        // Re-throw non-validation errors
        throw err;
      }
    }
  };

  return (
    <Stack
      sx={{
        minWidth: 446,
        maxWidth: 446,
        alignItems: "stretch",
        gap: "16px",
        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
        p: "24px",
        borderRadius: "8px",
        ul: {
          p: 0,
          li: { p: 0, mb: "4px", ":last-child": { m: 0 } }
        }
      }}
    >
      {measureOptions?.length && (
        <Stack direction="row" sx={{ gap: "7px" }}>
          <NumberFormat // Use NumberFormat instead of NumberInput!!!
            size="small"
            placeholder="Enter value"
            label="Value"
            fullWidth
            variant="outlined"
            value={selectedValue}
            disabled={!!isGroupParent && !!selectedPolygonsTag}
            decimalSeparator={numberFormat === "eu" ? "," : "."}
            thousandSeparator={numberFormat === "eu" ? "." : ","}
            allowNegative={!currentMeasure?.is_absolute || false}
            onValueChange={(values) => {
              const newValue = values.floatValue;
              if (onValueChange) {
                onValueChange(newValue || 0);
                if (validationErrors.value) {
                  setValidationErrors({ ...validationErrors, value: "" });
                }
              }
            }}
            customInput={TextField}
            error={getFieldError("value")}
            helperText={getFieldHelperText("value")}
          />
          <Autocomplete
            fullWidth
            options={measureOptions}
            value={currentMeasure}
            onChange={(_, option) => {
              measureOnChange(option?.id || null);
              if (option?.is_global_value) {
                invOnChange(null);
                roundOnChange(null);
                setSelectedGroups([]);
              }

              if (!option?.is_global_value && roundOpts.length === 1) {
                //if round options is only one, then set it as default
                roundOnChange(roundOpts[0].value);
              }

              if (!option?.is_global_value && !invValue && documentFileData.investor_id) {
                invOnChange(documentFileData.investor_id);
              }
              if (validationErrors.measure) {
                setValidationErrors({ ...validationErrors, measure: "" });
              }
            }}
            renderInput={(params) => <TextField {...params} placeholder="Select" label="Parameter" />}
            getOptionLabel={(option) => option.name}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: MeasureParameter) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
          />
        </Stack>
      )}
      {roundOpts.length > 1 && (
        <Stack direction="row" sx={{ gap: "10px", width: "100%" }} alignItems="center">
          <Dropdown
            label="Round"
            sx={{
              "& .MuiAutocomplete-clearIndicator": {
                display: "none"
              }
            }}
            disabled={!currentMeasure || isMeasureGlobalValue}
            placeholder="Select Round"
            options={roundOpts}
            loading={roundLoading}
            value={roundOpts.find((opt) => opt.value === roundVal)}
            onChange={(e, value) => {
              invOnChange(null);
              setSelectedGroups([]);
              roundOnChange(value?.value || null);
              if (validationErrors.round) {
                setValidationErrors({ ...validationErrors, round: "" });
              }
            }}
            error={getFieldError("round")}
          />
          {isMeasureGlobalValue && (
            <MaterialTooltip title="Round is invariant parameter for this measure" isShow>
              <InfoOutlinedIcon sx={{ fontSize: "20px" }} />
            </MaterialTooltip>
          )}
        </Stack>
      )}
      <Stack direction="row" sx={{ gap: "7px" }}>
        <DesktopDatePicker
          loading={dateLoading}
          format={dateFormat}
          views={["year", "month", "day"]}
          sx={{ flexBasis: "50%" }}
          value={dateValue ? dayjs(dateValue) : null}
          label="Date"
          slotProps={{
            textField: {
              placeholder: "Select",
              error: getFieldError("date"),
              helperText: getFieldHelperText("date")
            },
            popper: {
              disablePortal: true,
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    fallbackPlacements: ["top-start", "bottom-start"]
                  }
                },
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    boundary: "clippingParents"
                  }
                }
              ]
            }
          }}
          onChange={(date) => {
            if (date && date.isValid()) {
              dateOnChange(toIsoMidday(date?.toISOString()) || null);
              if (validationErrors.date) {
                setValidationErrors({ ...validationErrors, date: "" });
              }
            }
          }}
        />
        <Dropdown
          sx={{
            flexBasis: "50%",
            "& .MuiAutocomplete-clearIndicator": {
              display: "none"
            }
          }}
          label="Currency"
          disabled={isChild}
          placeholder="Select"
          options={currOpts}
          loading={currLoading}
          value={currValue ? currOpts.find((opt) => opt.value === currValue) : null}
          onChange={(e, value) => {
            currOnChange(value?.value || null);
            if (validationErrors.currency) {
              setValidationErrors({ ...validationErrors, currency: "" });
            }
          }}
          error={getFieldError("currency")}
          helperText={getFieldHelperText("currency")}
        />
      </Stack>
      <Stack direction="row" sx={{ gap: "10px", width: "100%" }} alignItems="center">
        {allow_split_by_group ? (
          <InvestorsGroupAutocomplete
            //can select single investor or investor groups
            // if selected investor, then groups will be empty
            //if selected groups, then investor will be null
            value={{
              investorId: invValue,
              groups: selectedGroups
            }}
            onChange={(value) => {
              invOnChange(value.investorId || null);
              if (validationErrors.investorsGroup || validationErrors.investor) {
                setValidationErrors({ ...validationErrors, investorsGroup: "", investor: "" });
              }
              setSelectedGroups(value.groups);
            }}
            onCreateInvestor={() => setIsDrawerOpen(true)}
            error={getFieldError("investorsGroup") || getFieldError("investor")}
            helperText={getFieldHelperText("investorsGroup") || getFieldHelperText("investor")}
            disabled={!currentMeasure || isGroupParent || !roundVal || isMeasureGlobalValue}
            dealInvestorIds={invOptions.filter((opt) => opt.value != ALL_INVESTORS_VALUE).map((opt) => opt.value)}
            //same as shouldFilterAllInvestors in investorsOptions
            allInvestorsCommitmentOption={!isMeasureGlobalValue && isSplittable && !isChild}
            sx={{ width: "100%" }}
          />
        ) : (
          <Autocomplete
            sx={{ width: "100%" }}
            disabled={
              !currentMeasure ||
              isGroupParent ||
              !roundVal ||
              isMeasureGlobalValue ||
              (selectedPolygonsTag && selectedPolygonsTag.investor?.id === ALL_INVESTORS_VALUE)
            }
            loading={invLoading}
            options={investorsOptions}
            value={
              isGroupParent
                ? ({ value: -1, label: "Multiple" } as InvestorOption)
                : investorsOptions.find((opt) => !("isSeparator" in opt) && opt.value === invValue) || null
            }
            onChange={(e, value) => {
              if (value && !("isSeparator" in value)) {
                invOnChange(value.value);
                setSplitType(value.value === ALL_INVESTORS_VALUE ? "all_investors" : null);
                if (validationErrors.investor) {
                  setValidationErrors({ ...validationErrors, investor: "" });
                }
              }
            }}
            PaperComponent={({ children }) => {
              return (
                <Paper
                  sx={{
                    position: "absolute",
                    width: "100%",
                    backgroundColor: "white",
                    zIndex: "10",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-listbox": {
                      maxHeight: "28vh",
                      "& .MuiAutocomplete-option[aria-disabled='true']": {
                        opacity: 1,
                        "&.Mui-focused": {
                          opacity: 1
                        }
                      },
                      "& .MuiAutocomplete-option[aria-selected='true']": {
                        bgcolor: "#edf1f9",
                        "&.Mui-focused": {
                          bgcolor: "#edf1f9"
                        }
                      }
                    },
                    "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                      bgcolor: "#edf1f9"
                    }
                  }}
                >
                  <>
                    {children}
                    <Button
                      onMouseDown={() => {
                        setIsDrawerOpen(true);
                      }}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Investor
                    </Button>
                  </>
                </Paper>
              );
            }}
            renderOption={(props, option) => {
              if ("isSeparator" in option) {
                return (
                  <Box
                    key={option.label}
                    display="flex"
                    alignItems="center"
                    padding="6px 20px"
                    sx={{ pointerEvents: "none" }}
                  >
                    <Typography fontSize="12px" fontWeight="bold" sx={{ color: "#496682" }}>
                      {option.label}
                    </Typography>
                  </Box>
                );
              }
              return (
                <li style={{ height: "43px", fontSize: "16px" }} {...props} key={option.value}>
                  {option.label}
                </li>
              );
            }}
            isOptionEqualToValue={(option, value) => {
              if ("isSeparator" in option || (value && "isSeparator" in value)) return false;
              if (!value || "isSeparator" in value) return false;
              return option.value === value.value;
            }}
            getOptionDisabled={(option) => "isSeparator" in option}
            renderInput={(params) => {
              return (
                <TextField
                  placeholder="Select"
                  {...params}
                  label="Investors"
                  error={getFieldError("investor")}
                  helperText={getFieldHelperText("investor")}
                />
              );
            }}
          />
        )}
        {isMeasureGlobalValue && (
          <MaterialTooltip title="An investor invariant parameter" isShow>
            <InfoOutlinedIcon sx={{ fontSize: "20px" }} />
          </MaterialTooltip>
        )}
      </Stack>
      <Stack flexDirection="column">
        {!isChild && !isMeasureGlobalValue && invValue && isSplittable && !selectedPolygonsTag && (
          <FormControlLabel
            sx={{ width: "260px" }}
            control={
              <Checkbox
                checked={splitType === "single_investor" || invValue === ALL_INVESTORS_VALUE}
                checkedIcon={<CheckBoxIcon sx={{ fill: "#071F35" }} />}
                onChange={() => setSplitType((prev) => (!prev ? "single_investor" : null))}
              />
            }
            label="Split By Investor Commitment"
          />
        )}

        {!isChild &&
          !isMeasureGlobalValue &&
          !invValue &&
          allow_split_by_group &&
          Boolean(selectedGroups.length) &&
          !selectedPolygonsTag && (
            <>
              <RadioGroup
                value={splitTypeGroup || ""}
                onChange={(e) => setSplitTypeGroup(e.target.value as TaggerSplitByGroup)}
              >
                <FormControlLabel
                  value="entire_commitment"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#071F35" } }} />}
                  label="Split By Entire Commitment"
                />
                <FormControlLabel
                  value="group_commitment"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#071F35" } }} />}
                  label="Split By Group Commitment"
                />
              </RadioGroup>
              {getFieldError("split_by") && (
                <Typography color="error" fontSize="12px" fontWeight="bold">
                  {getFieldHelperText("split_by")}
                </Typography>
              )}
            </>
          )}
        {!isChild && (
          <FormControlLabel
            sx={{ width: "150px" }}
            control={
              <Checkbox
                checked={isDraft}
                checkedIcon={<CheckBoxIcon sx={{ fill: "#071F35" }} />}
                onChange={() => setIsDraft((prev) => !prev)}
              />
            }
            label="Mark as Draft"
          />
        )}
      </Stack>
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          setCreatedInvestors([...createdInvestors, { value: response.id, label: response.name }]);
          invOnChange(response.id);
        }}
      />
      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!currentMeasure} variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};

export const TagMenu: React.FC<TagMenuProps> = (props) => {
  return (
    <Popper
      open={props.open}
      anchorEl={props.anchorEl}
      sx={{
        zIndex: 1000,
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)"
      }}
      // onClose={props.onClose}
      modifiers={[
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top", "bottom", "right", "left"]
          }
        },
        {
          name: "offset",
          options: {
            offset: [0, 4]
          }
        }
      ]}
    >
      <ClickAwayListener onClickAway={props.onClose}>
        <div>
          <TagMenuContent {...props} />
        </div>
      </ClickAwayListener>
    </Popper>
  );
};
